import { postVitals, VITALS_ROUTES } from '@vinyl/shared';

type Route = (typeof VITALS_ROUTES)[number];
type Report = { route: Route; deviceClass: 'phone' | 'desktop'; lcp?: number; inp?: number; cls?: number };

/** The route template of a path; ids never leave the browser. */
export function routeTemplate(pathname: string): Route {
  const path = pathname
    .replace(/\/+$/, '')
    .replace(/^\/copies\/[^/]+\/edit$/, '/copies/$id/edit')
    .replace(/^\/releases\/[^/]+\/edit$/, '/releases/$id/edit');
  return (VITALS_ROUTES as readonly string[]).includes(path) ? (path as Route) : 'other';
}

function send(report: Report): void {
  void fetch(postVitals.path, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(report),
  }).catch(() => undefined);
}

/**
 * Measures largest contentful paint, interaction delay (the slowest
 * interaction) and layout shift for the page as first loaded, and reports
 * them once when the page is hidden (app-administration, Real-world speed).
 */
export function installVitals(post: (report: Report) => void = send): void {
  if (typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes) return;
  const route = routeTemplate(window.location.pathname);
  const deviceClass = window.matchMedia('(max-width: 767px)').matches ? 'phone' : 'desktop';
  let lcp: number | undefined;
  let inp: number | undefined;
  let cls = 0;
  const observe = (type: string, onEntry: (entry: PerformanceEntry & { value?: number; hadRecentInput?: boolean }) => void, extra: object = {}) => {
    if (!PerformanceObserver.supportedEntryTypes.includes(type)) return;
    new PerformanceObserver((list) => list.getEntries().forEach((e) => onEntry(e))).observe({ type, buffered: true, ...extra });
  };
  observe('largest-contentful-paint', (e) => (lcp = e.startTime));
  observe('layout-shift', (e) => {
    if (!e.hadRecentInput) cls += e.value ?? 0;
  });
  observe('event', (e) => (inp = Math.max(inp ?? 0, e.duration)), { durationThreshold: 40 });

  let sent = false;
  document.addEventListener('visibilitychange', () => {
    if (sent || document.visibilityState !== 'hidden' || lcp === undefined) return;
    sent = true;
    post({
      route,
      deviceClass,
      lcp: Math.round(lcp),
      ...(inp !== undefined ? { inp: Math.round(inp) } : {}),
      cls: Math.round(cls * 10_000) / 10_000,
    });
  });
}
